import { RouterProvider } from "@tanstack/react-router";
import { lazy, Suspense } from "react";

import { AppErrorView } from "./components/AppErrorView";
import { RenderErrorBoundary } from "./components/RenderErrorBoundary";
import { SshHostKeyPromptDialog } from "./components/SshHostKeyPromptDialog";
import { SshPasswordPromptDialog } from "./components/SshPasswordPromptDialog";
import { AppAtomRegistryProvider } from "./rpc/atomRegistry";
import type { AppRouter } from "./router";

const ElectronBrowserHost = lazy(() =>
  import("./browser/ElectronBrowserHost").then((module) => ({
    default: module.ElectronBrowserHost,
  })),
);
const PreviewAutomationHosts = lazy(() =>
  import("./components/preview/PreviewAutomationHosts").then((module) => ({
    default: module.PreviewAutomationHosts,
  })),
);

const renderAppError = (error: unknown) => <AppErrorView error={error} />;

/**
 * Owns renderer-wide providers. The Electron browser host sits outside the
 * router so webviews admitted by Workspace Surface Host leases can survive
 * permitted surface switches while sharing the routed UI atom registry.
 */
export function AppRoot({ router }: { readonly router: AppRouter }) {
  return (
    // The router catches its own routes; anything thrown out here would otherwise unmount the
    // whole tree and leave an empty window (#1018).
    <RenderErrorBoundary fallback={renderAppError}>
      <AppAtomRegistryProvider>
        <RouterProvider router={router} />
        {/* Outside the router: a forward can ask for a password, or about a destination's identity
            on first contact, during any route — including one that is itself waiting on the
            connection being opened. */}
        <SshPasswordPromptDialog />
        <SshHostKeyPromptDialog />
        <Suspense fallback={null}>
          <PreviewAutomationHosts />
          <ElectronBrowserHost />
        </Suspense>
      </AppAtomRegistryProvider>
    </RenderErrorBoundary>
  );
}
